import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getFloorplanUploadSignedUrl } from '../services/getFloorplanUploadSignedUrl.service';
import { postCreateFloorplan } from '../services/postCreateFloorplan.service';
import getFileExtension from 'utils/getFileExtension';
import uuidv4 from 'uuid/v4';

const EXTENSION_BY_TYPE = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
};

/**
 * The extension a file is stored under: its name's, or for a file without a
 * name (the image a PDF page was turned into) its type's.
 */
export const fileExtensionOf = (file) =>
  (file.name && getFileExtension(file.name)) ||
  EXTENSION_BY_TYPE[file.type] ||
  'bin';

const putToS3 = async ({ projectId, file, contentType, onUploadProgress }) => {
  const s3FileName = `${uuidv4()}.${fileExtensionOf(file)}`;
  const signedUrl = await getFloorplanUploadSignedUrl({
    projectId,
    fileName: s3FileName,
    contentType,
  });
  await axios.put(signedUrl, file, {
    headers: { 'Content-Type': contentType },
    onUploadProgress,
  });
  return s3FileName;
};

/**
 * Uploads a floorplan image and creates its row. With `source` (the PDF the
 * image was made from, and the page's size) the PDF is kept next to the image
 * and the row says so; if only the PDF fails to upload, the floorplan is
 * still made from the image, as before.
 */
export const uploadFloorplan = createAsyncThunk(
  'floorplans/uploadFloorplan',
  async (
    {
      projectId,
      name,
      file,
      source = null,
      onProgress,
      drawingType,
      level,
      floorHeight,
      scale,
      facing,
    },
    { rejectWithValue }
  ) => {
    try {
      // One progress bar over both files, by size.
      const total = file.size + (source ? source.file.size : 0) || 1;
      let done = 0;
      const progressFrom = (offset) => (progressEvent) => {
        done = offset + progressEvent.loaded;
        if (onProgress) {
          onProgress(Math.min(100, Math.round((done / total) * 100)));
        }
      };

      const s3FileName = await putToS3({
        projectId,
        file,
        contentType: file.type,
        onUploadProgress: progressFrom(0),
      });

      let sourceFields = null;
      if (source) {
        try {
          const sourceFileName = await putToS3({
            projectId,
            file: source.file,
            contentType: 'application/pdf',
            onUploadProgress: progressFrom(file.size),
          });
          sourceFields = {
            s3FileName: sourceFileName,
            contentType: 'application/pdf',
            page: source.page,
            pageCount: source.pageCount,
            pageWidthMm: source.pageWidthMm,
            pageHeightMm: source.pageHeightMm,
          };
        } catch (err) {
          sourceFields = null;
        }
      }

      // The 3D view's fields go along when the dialog gave them; the API
      // leaves a missing one unset.
      const drawingFields = Object.fromEntries(
        Object.entries({
          drawingType,
          level,
          floorHeight,
          scale,
          facing,
        }).filter(([, value]) => value !== undefined && value !== null)
      );

      const floorplan = await postCreateFloorplan(projectId, {
        name,
        s3FileName,
        contentType: file.type,
        ...drawingFields,
        ...(sourceFields ? { source: sourceFields } : {}),
      });

      return floorplan;
    } catch (err) {
      return rejectWithValue({ message: err.message });
    }
  }
);
